import { apiService } from '@/shared/services/apiServices';
import type { TaskFormApiSection } from '../types';

const FORM_MANAGEMENT_BASE_PATH = 'loopay-form-management/forms';
const TASK_FORM_BASE_PATH = 'loopay-task-form-management/task-form';
const MEDIA_PRESIGNED_BATCH_PATH =
  'loopay-media-management/media/presigned-urls/batch';
const MEDIA_DELETE_PATH = 'loopay-media-management/media/resources';
const USER_PROFILE_PATH = 'loopay-user-management/profile';

/**
 * API endpoint paths for the task form fill flow.
 *
 * @example
 * fillTaskFormEndpoints.FORM_SCHEMA('abc-123')
 * // 'loopay-form-management/forms/abc-123'
 *
 * fillTaskFormEndpoints.TASK_FORM('abc-123')
 * // 'loopay-task-form-management/task-form/abc-123'
 */
export const fillTaskFormEndpoints = {
  /** GET form template schema by UUID (form_templates.id). */
  FORM_SCHEMA: (formUuid: string) => `${FORM_MANAGEMENT_BASE_PATH}/${formUuid}`,
  /** POST submission for a service request. */
  TASK_FORM: (serviceRequestId: string) =>
    `${TASK_FORM_BASE_PATH}/${serviceRequestId}`,
} as const;

// ─── Schema response types ────────────────────────────────────────────────────

/** Full form template returned by GET /forms/{uuid} (form-management service). */
export interface GetFormSchemaResponseData {
  /** Form template UUID (primary key). */
  id: string;
  /** Human-readable business ID, e.g. FORM000000001. */
  form_id: string;
  name: string;
  description: string | null;
  current_version: number;
  created_at: string;
  updated_at: string;
  schema: { sections: TaskFormApiSection[] };
}

export interface GetFormSchemaResult {
  success: boolean;
  data?: GetFormSchemaResponseData;
  error?: string;
}

// ─── GET task-form response types ─────────────────────────────────────────────

/**
 * Shape of the data block returned by GET /task-form/{serviceRequestId}.
 * Used to pre-populate the technician's existing profile signature.
 */
export interface GetTaskFormResponseData {
  schema: { sections: TaskFormApiSection[] } | null;
  submission: unknown | null;
  technician_profile_signature: TechnicianProfileSignature | null;
}

export interface GetTaskFormResult {
  success: boolean;
  data?: GetTaskFormResponseData;
  error?: string;
}

// ─── Technician profile signature ────────────────────────────────────────────

/**
 * Existing signature stored on the technician's profile (`technicians.signature`).
 * Does not carry signer audit fields — those are added at task-form submission time.
 */
export interface TechnicianProfileSignature {
  isUploaded: boolean;
  path: string;
  resourceId: string;
  filename: string;
  fileType: string;
  fileSize: number;
}

// ─── Signature media types ────────────────────────────────────────────────────

/**
 * Full signature snapshot sent to the backend on submission.
 *
 * Must be uploaded to S3 via a presigned URL before calling submitTaskForm.
 * Files are only persisted if the form submission transaction commits.
 *
 * @property isUploaded - Always true when included in a submission.
 * @property path       - S3 object key (no leading slash).
 */
export interface SignatureMedia {
  isUploaded: true;
  path: string;
  resourceId: string;
  filename: string;
  fileType: string;
  fileSize: number;
}

// ─── Submit request types ─────────────────────────────────────────────────────

/** POST body for submitting the task form. */
export interface SubmitTaskFormBody {
  field_inputs: Record<string, unknown>;
  equipment_id?: string | null;
  /** Canvas-drawn client signature — mandatory. */
  client_signature: SignatureMedia;
  /** Technician's uploaded signature file — mandatory. */
  technician_signature: SignatureMedia;
  notes?: string | null;
}

export interface SubmitTaskFormData {
  submission_id: string;
  service_request_id: string;
  status: string;
}

export interface SubmitTaskFormResult {
  success: boolean;
  data?: SubmitTaskFormData;
  error?: string;
  isForbidden?: boolean;
}

// ─── Presigned URL types ──────────────────────────────────────────────────────

interface SignaturePresignedFile {
  originalPath: string;
  path: string;
  url: string;
  resourceId: string;
}

export interface GenerateSignaturePresignedResult {
  success: boolean;
  data?: { files: SignaturePresignedFile[] };
  error?: string;
}

/** File entry returned by the batch presigned-URL endpoint for an attachment. */
export interface AttachmentPresignedFile {
  /** Original path passed in the request. */
  originalPath: string;
  /** Final resolved S3 key. */
  path: string;
  /** Presigned PUT URL (signed with x-amz-tagging: status=provisional). */
  url: string;
}

export interface GenerateAttachmentPresignedResult {
  success: boolean;
  data?: { files: AttachmentPresignedFile[] };
  error?: string;
}

// ─── Helper ──────────────────────────────────────────────────────────────────

function extractErrorMessage(data: unknown): string {
  if (data !== null && typeof data === 'object' && 'message' in data) {
    return String((data as { message: unknown }).message);
  }
  return '';
}

// ─── API functions ────────────────────────────────────────────────────────────

/**
 * Fetches the form template schema by its UUID from the form-management service.
 * Used to determine which fields and field types to render in the fill wizard.
 *
 * The `formUuid` is `task_form.id` from the service request detail response.
 *
 * @param formUuid - UUID primary key of the form template (form_templates.id).
 */
export async function getFormSchema(
  formUuid: string
): Promise<GetFormSchemaResult> {
  const result = await apiService.getAPI(
    fillTaskFormEndpoints.FORM_SCHEMA(formUuid)
  );
  if (result.error || result.isNetworkError) {
    const msg = extractErrorMessage(result.data);
    return {
      success: false,
      error: msg || 'Nie udało się pobrać szablonu formularza.',
    };
  }
  // form-management returns the schema object directly (no { data: ... } wrapper)
  const payload = result.data as GetFormSchemaResponseData;
  return { success: true, data: payload };
}

/**
 * Fetches the task-form context for a service request.
 *
 * Returns the locked form schema, any existing submission, and the
 * technician's profile signature — used to pre-populate the tech-sig section
 * so the technician does not have to re-upload on every form fill.
 *
 * @param serviceRequestId - Internal UUID of the service request.
 */
export async function getTaskFormData(
  serviceRequestId: string
): Promise<GetTaskFormResult> {
  const result = await apiService.getAPI(
    fillTaskFormEndpoints.TASK_FORM(serviceRequestId)
  );
  if (result.error || result.isNetworkError) {
    const msg = extractErrorMessage(result.data);
    return {
      success: false,
      error: msg || 'Nie udało się pobrać danych formularza zadania.',
    };
  }
  const payload = result.data as { data: GetTaskFormResponseData };
  return { success: true, data: payload.data };
}

/**
 * Submits filled form answers for the assigned service request.
 * On success the SR transitions to COMPLETED within the same transaction.
 *
 * Allowed source statuses: `accepted`, `in_progress`.
 *
 * @param serviceRequestId - Internal UUID of the service request.
 * @param body             - Submitted field inputs and optional metadata.
 */
export async function submitTaskForm(
  serviceRequestId: string,
  body: SubmitTaskFormBody
): Promise<SubmitTaskFormResult> {
  const result = await apiService.postAPI(
    fillTaskFormEndpoints.TASK_FORM(serviceRequestId),
    body
  );
  if (result.error || result.isNetworkError) {
    if (result.status === 403) {
      return { success: false, isForbidden: true };
    }
    const msg = extractErrorMessage(result.data);
    return {
      success: false,
      error: msg || 'Nie udało się przesłać formularza zadania.',
    };
  }
  const payload = result.data as { data: SubmitTaskFormData };
  return { success: true, data: payload.data };
}

/**
 * Generates presigned S3 upload URLs for signature files attached to a task form submission.
 *
 * @param paths - Desired S3 key hints for each file (one per signature).
 */
export async function generateSignaturePresignedUrls(
  paths: string[]
): Promise<GenerateSignaturePresignedResult> {
  const result = await apiService.postAPI(MEDIA_PRESIGNED_BATCH_PATH, {
    paths,
    provisional: true,
  });
  if (result.error || result.isNetworkError) {
    return {
      success: false,
      error: 'Nie udało się wygenerować adresów URL do przesyłania.',
    };
  }
  return {
    success: true,
    data: result.data as { files: SignaturePresignedFile[] },
  };
}

/**
 * Deletes a provisional S3 attachment uploaded during task-form fill.
 *
 * Uses the existing DELETE /media/resources endpoint, which skips the DB
 * step for `service-requests` entity type and delegates directly to S3 delete.
 * Fire-and-forget safe — failures are swallowed since the S3 lifecycle rule
 * will expire the object after 7 days anyway.
 *
 * @param s3Key - The S3 object key (e.g. `service-requests/…/attachments/…`).
 */
export async function deleteProvisionalAttachment(
  s3Key: string
): Promise<void> {
  await apiService.deleteAPI(MEDIA_DELETE_PATH, { path: s3Key });
}

export interface UpdateProfileSignatureResult {
  success: boolean;
  error?: string;
}

/**
 * Persists a newly-uploaded signature to the technician's own profile.
 *
 * Calls PATCH /loopay-user-management/profile with the `signature` field.
 * Must be called AFTER the file is already uploaded to S3 (isUploaded: true)
 * so the DB record is written with a live, permanent S3 key.
 * Do NOT pass a provisional-tagged S3 key here — the profile record is
 * permanent and the lifecycle rule would later delete the file.
 *
 * @param signature - Fully uploaded signature media descriptor.
 */
export async function updateProfileSignature(
  signature: SignatureMedia
): Promise<UpdateProfileSignatureResult> {
  const result = await apiService.patchAPI(USER_PROFILE_PATH, { signature });
  if (result.error || result.isNetworkError) {
    return {
      success: false,
      error: 'Nie udało się zaktualizować podpisu w profilu.',
    };
  }
  return { success: true };
}

/**
 * Generates a presigned S3 upload URL for a technician profile signature.
 *
 * Unlike task-form signatures, profile signatures are NOT tagged provisional —
 * they are committed immediately to the technician profile and must not be
 * expired by the S3 lifecycle rule.
 *
 * @param paths - S3 key paths for the signature file (one per call).
 */
export async function generateProfileSignaturePresignedUrl(
  paths: string[]
): Promise<GenerateSignaturePresignedResult> {
  const result = await apiService.postAPI(MEDIA_PRESIGNED_BATCH_PATH, {
    paths,
  });
  if (result.error || result.isNetworkError) {
    return {
      success: false,
      error: 'Nie udało się wygenerować adresu URL do przesyłania podpisu.',
    };
  }
  return {
    success: true,
    data: result.data as { files: SignaturePresignedFile[] },
  };
}

/**
 * Generates presigned S3 upload URLs for task-form attachment files.
 *
 * URLs are signed with `x-amz-tagging: status=provisional` so that
 * abandoned uploads (tab close, network loss before submit) are
 * automatically expired by the S3 lifecycle rule after 7 days.
 * On successful form submission the backend retags them as `confirmed`.
 *
 *
 * @param paths - Up to 10 S3 key paths per call.
 */
export async function generateAttachmentPresignedUrls(
  paths: string[]
): Promise<GenerateAttachmentPresignedResult> {
  const result = await apiService.postAPI(MEDIA_PRESIGNED_BATCH_PATH, {
    paths,
    provisional: true,
  });
  if (result.error || result.isNetworkError) {
    return {
      success: false,
      error:
        'Nie udało się wygenerować adresów URL do przesyłania załączników.',
    };
  }
  return {
    success: true,
    data: result.data as { files: AttachmentPresignedFile[] },
  };
}
